import { Text } from 'src/modules/shared/components/atoms/text';
import { StyledIcon } from 'src/modules/shared/components/atoms/icons/styled-icon';
import { PiChatCircle, PiUser } from 'react-icons/pi';
import {
  type CustomFieldType,
  type FunnelConversationDocument,
} from 'src/api/generated';
import { getCustomFieldKey } from 'src/modules/shared/utils/custom-fields.util';
import { type TableColumnProps } from 'src/modules/shared/components/molecules/table/table';
import { tableVariantClasses } from 'src/modules/shared/components/molecules/table/styles';
import { CustomFieldTableValue } from 'src/modules/shared/components/molecules/custom-fields/custom-field-table-value';

interface CustomFieldDefinition {
  fieldKey: string;
  fieldLabel?: string;
  fieldType: CustomFieldType;
  includeTime?: boolean;
}

interface CustomFieldColumnsParams {
  customFields: CustomFieldDefinition[];
  variant?: keyof typeof tableVariantClasses;
}

export const createConversationCustomFieldColumns = ({
  customFields,
  variant = 'primary',
}: CustomFieldColumnsParams): TableColumnProps<FunnelConversationDocument>[] => {
  const { text } = tableVariantClasses[variant];

  return customFields.map((field) => ({
    header: (
      <div className="flex items-center gap-1.5">
        <StyledIcon
          icon={<PiChatCircle className="w-3.5 h-3.5" />}
          variant="secondary"
          size="xs"
        />
        <Text size="small" weight="medium" className={text}>
          {field.fieldLabel ?? field.fieldKey}
        </Text>
      </div>
    ),
    accessor: `customFields.${field.fieldKey}`,
    className: 'min-w-[140px]',
    render: (_value: unknown, row: FunnelConversationDocument) => {
      if (!row.customFields) return null;

      const fieldKey = getCustomFieldKey(field.fieldType, field.fieldKey);
      const customFieldValues = row.customFields as Record<string, unknown>;
      const fieldValue = customFieldValues[fieldKey];

      return (
        <CustomFieldTableValue
          value={fieldValue}
          fieldType={field.fieldType}
          includeTime={field.includeTime}
          variant={variant}
        />
      );
    },
  }));
};

export const createContactCustomFieldColumns = ({
  customFields,
  variant = 'primary',
}: CustomFieldColumnsParams): TableColumnProps<FunnelConversationDocument>[] => {
  const { text } = tableVariantClasses[variant];

  return customFields.map((field) => ({
    header: (
      <div className="flex items-center gap-1.5">
        <StyledIcon
          icon={<PiUser className="w-3.5 h-3.5" />}
          variant="secondary"
          size="xs"
        />
        <Text size="small" weight="medium" className={text}>
          {field.fieldLabel ?? field.fieldKey}
        </Text>
      </div>
    ),
    accessor: `contacts.customFields.${field.fieldKey}`,
    className: 'min-w-[120px]',
    render: (_value: unknown, row: FunnelConversationDocument) => {
      const customFieldValues = row.contacts?.[0]?.customFields as
        | Record<string, unknown>
        | undefined;
      if (!customFieldValues) {
        return null;
      }

      const fieldKey = getCustomFieldKey(field.fieldType, field.fieldKey);
      const fieldValue = customFieldValues[fieldKey];

      return (
        <CustomFieldTableValue
          value={fieldValue}
          fieldType={field.fieldType}
          includeTime={field.includeTime}
          variant={variant}
        />
      );
    },
  }));
};
